import { Decimal, useIntl, WalletLimits } from '@capsicum/common';
import { useCallback, useMemo } from 'react';

import { DEFAULT_MIN_SELLABLE_QUANTITY, type ErrorMessage } from '../models';

export const useSellLimits = () => {
	const { $t } = useIntl();

	const errorMessage = useMemo<ErrorMessage>(
		() => ({
			[WalletLimits.InsufficientBalance]: { message: $t('marketplace.limits.insufficientBalance') },
			[WalletLimits.OnHold]: {
				message: $t('marketplace.limits.stakedFanTokens'),
				tooltip: $t('marketplace.limits.stakedFanTokensTooltip')
			}
		}),
		[$t]
	);

	const getSellLimit = useCallback(
		(maxAmount = '0', stackedAmount = '0', quantityAmount = String(DEFAULT_MIN_SELLABLE_QUANTITY)) => {
			const max = new Decimal(maxAmount);
			const stacked = new Decimal(stackedAmount);
			const amount = new Decimal(quantityAmount);

			if (amount.lessThanOrEqualTo(max)) return null;

			// If the amount is greater than the max and user has tokens OnHold, then show the OnHold error.
			if (max.lessThan(amount) && amount.lessThanOrEqualTo(max.add(stacked))) return WalletLimits.OnHold;

			// If the amount is greater than max + OnHold, then show the InsufficientBalance error.
			return WalletLimits.InsufficientBalance;
		},
		[]
	);

	const getValidation = useCallback(
		(maxAmount = '0', stackedAmount = '0', amount = String(DEFAULT_MIN_SELLABLE_QUANTITY)) => {
			const validationError = getSellLimit(maxAmount, stackedAmount, amount);

			// If the amount is less than or equal to the max, then there is no error.
			return validationError === null
				? { message: '' }
				: { validationType: validationError, ...errorMessage[validationError] };
		},
		[getSellLimit, errorMessage]
	);

	return {
		DEFAULT_MIN_SELLABLE_QUANTITY,
		getSellLimit,
		getValidation
	};
};
